import styled from '@emotion/styled';
import React from 'react';
import { useConfig, useSettings } from '@vmos2/providers';
import { useLocale } from '@vmos2/utils';
import { useTranslation } from 'react-i18next';

import { usePriceContext } from './priceProvider';

type Props = {
  ariaHidden?: boolean,
  children: number,
  dataTest?: string,
  id?: string,
  isNegative?: boolean,
  isPositive?: boolean,
  showCurrencySign?: boolean,
};

export const Price = ({ ariaHidden, children, dataTest, id, isNegative, isPositive, showCurrencySign }: Props) => {
  const value = usePriceContext();
  const config = useConfig();
  const settings = useSettings();
  const { locale } = useLocale(config, settings);
  const { t } = useTranslation('common');

  const format =
    value.showCurrencySign || showCurrencySign
      ? { currency: value.currency, style: 'currency' }
      : { minimumFractionDigits: 2 };

  const price = new Intl.NumberFormat(locale, format).format(children);
  const dataPriceAttribute = `${isNegative ? t('minus', { price }) : ''}${isPositive ? t('plus', { price }) : ''}`;
  const isPositiveOrNegative = isNegative || isPositive;

  return (
    <Wrapper {...(ariaHidden && { 'aria-hidden': ariaHidden })} data-test={dataTest || 'price'} id={id}>
      {isPositiveOrNegative && <span className="hide-text-show-focus">{dataPriceAttribute || price}</span>}

      <span {...(isPositiveOrNegative && { 'aria-hidden': true })}>
        {isNegative && '−'}
        {isPositive && '+'}
        {price}
      </span>
    </Wrapper>
  );
};

const Wrapper = styled.span`
  position: relative;
  white-space: nowrap;
`;
